import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { PortableAssetGroup } from '../../../API/metadata';
import { isDefined } from '../../../util/isDefined';
import { VOID } from '../../../util/typeUtils';
import { EditModeNavGraph } from '../../NavGraph/EditModeNavGraph';
import { NavGraph, NavGraphLink, NavGraphNode } from '../../NavGraph/NavGraph';
import { EmissionLimit } from '../../api/EmissionLimit';
import { LocatedAsset } from '../../api/LocatedAsset';
import { Microfence } from '../../api/Microfence';
import { MicrofencePair } from '../../api/MicrofencePair';
import {
  Navmesh,
  NavmeshArea,
  NavmeshLevel,
  NavmeshLink,
  NavmeshNode,
  NavmeshEmissionZone,
} from '../../api/Navmesh';
import { PortableAsset } from '../../api/PortableAsset';
import { ServiceConfig } from '../../api/ServiceConfig';
import { UntrackedAsset } from '../../api/UntrackedAsset';
import { ToolType } from './EditTools';

export type PortableAssetForEdit = Omit<PortableAsset, 'derived'>;
export type PortableAssetGroupForEdit = Omit<PortableAssetGroup, 'id'> & { id: string };

export type CurrentEdit =
  | { type: 'located'; asset: LocatedAsset }
  | { type: 'portable'; asset: PortableAssetForEdit }
  | { type: 'portableAssetGroup'; asset: PortableAssetGroupForEdit & { uuid?: never } }
  | { type: 'untracked'; asset: UntrackedAsset }
  | { type: 'link'; asset: NavGraphLink & { uuid?: never } }
  | { type: 'node'; asset: NavGraphNode & { uuid?: never } }
  | { type: 'level'; asset: NavmeshLevel & { uuid?: never } }
  | { type: 'area'; asset: NavmeshArea & { uuid?: never } }
  | { type: 'emissionZone'; asset: NavmeshEmissionZone & { uuid?: never }; limit: EmissionLimit }
  | { type: 'new link'; asset: NavGraphLink & { uuid?: never }; linkTo?: NavGraphNode }
  | { type: 'service config'; asset: ServiceConfig & { uuid?: never } }
  | { type: 'microfence'; asset: (Microfence | MicrofencePair) & { uuid?: never } };

export type BulkEdit = 'portable' | 'navmesh' | 'tagboard';

export type CurrentDeletion =
  | LocatedAsset
  | PortableAssetForEdit
  | (PortableAssetGroupForEdit & { uuid: '' })
  | UntrackedAsset
  | ServiceConfig
  | Microfence
  | MicrofencePair;

export type DeleteAssetByUuid = { uuid: string; delete: true; clientId: string; projectId: string };
export type RevertChangeByUuid = { uuid: string; revert: true };
export type DeleteAssetById = { id: string; delete: true; clientId: string; projectId: string };
export type RevertChangeById = { id: string; revert: true };

export type ClearChanges = { clearAll: true };

export type LocatedAssetEdit = LocatedAsset | DeleteAssetByUuid | undefined;
export type PortableAssetEdit = PortableAssetForEdit | DeleteAssetByUuid | undefined;
export type UntrackedAssetEdit = UntrackedAsset | DeleteAssetByUuid | undefined;
export type LocatedAssetsUpdate =
  | LocatedAsset
  | LocatedAsset[]
  | DeleteAssetByUuid
  | RevertChangeByUuid
  | ClearChanges;
export type PortableAssetsUpdate =
  | PortableAssetForEdit
  | PortableAssetForEdit[]
  | DeleteAssetByUuid
  | DeleteAssetByUuid[]
  | RevertChangeByUuid
  | ClearChanges;
export type UntrackedAssetsUpdate =
  | UntrackedAsset
  | UntrackedAsset[]
  | DeleteAssetByUuid
  | RevertChangeByUuid
  | ClearChanges;

export type PortableAssetGroupEdit = PortableAssetGroupForEdit | DeleteAssetById | undefined;
export type PortableAssetGroupUpdate =
  | PortableAssetGroupForEdit
  | PortableAssetGroupForEdit[]
  | DeleteAssetById
  | RevertChangeById
  | ClearChanges;

export type MicrofenceEdit = Microfence | DeleteAssetById | undefined;
export type MicrofencePairEdit = MicrofencePair | DeleteAssetById | undefined;
export type MicrofenceUpdate =
  | Microfence
  | Microfence[]
  | DeleteAssetById
  | RevertChangeById
  | ClearChanges;
export type MicrofencePairUpdate =
  | MicrofencePair
  | MicrofencePair[]
  | DeleteAssetById
  | RevertChangeById
  | ClearChanges;

export type ServiceConfigEdit = ServiceConfig | DeleteAssetById | undefined;
export type ServiceConfigUpdate = ServiceConfig | DeleteAssetById | RevertChangeById | ClearChanges;

export type DeleteNavmeshItem = { id: number; delete: true };
export type RevertNavmeshChange = { id: number; revert: true };
export type NavmeshLinkEdit = NavmeshLink | DeleteNavmeshItem | RevertNavmeshChange | undefined;
export type NavmeshNodeEdit = NavmeshNode | DeleteNavmeshItem | RevertNavmeshChange | undefined;
export type NavmeshLevelEdit = NavmeshLevel | DeleteNavmeshItem | RevertNavmeshChange | undefined;
export type NavmeshAreaEdit = NavmeshArea | DeleteNavmeshItem | RevertNavmeshChange | undefined;
export type NavmeshEmissionZoneEdit =
  | NavmeshEmissionZone
  | DeleteNavmeshItem
  | RevertNavmeshChange
  | undefined;
export type NavmeshLinkUpdate =
  | NavmeshLink
  | NavmeshLink[]
  | DeleteNavmeshItem
  | DeleteNavmeshItem[]
  | RevertNavmeshChange
  | RevertNavmeshChange[]
  | ClearChanges;
export type NavmeshNodeUpdate =
  | NavmeshNode
  | NavmeshNode[]
  | DeleteNavmeshItem
  | DeleteNavmeshItem[]
  | RevertNavmeshChange
  | ClearChanges;
export type NavmeshLevelUpdate =
  | NavmeshLevel
  | NavmeshLevel[]
  | DeleteNavmeshItem
  | RevertNavmeshChange
  | ClearChanges;
export type NavmeshAreaUpdate =
  | NavmeshArea
  | NavmeshArea[]
  | DeleteNavmeshItem
  | RevertNavmeshChange
  | ClearChanges;
export type NavmeshEmissionZoneUpdate =
  | NavmeshEmissionZone
  | NavmeshEmissionZone[]
  | DeleteNavmeshItem
  | RevertNavmeshChange
  | ClearChanges;
export type NavmeshUnitEdit = 'm' | 'ft' | undefined;

export type EmissionLimitEdit = EmissionLimit | DeleteAssetByUuid | undefined;
export type EmissionLimitUpdate =
  | EmissionLimit
  | EmissionLimit[]
  | DeleteAssetByUuid
  | RevertChangeByUuid
  | ClearChanges;

export type SetCurrentEditFn = (cur?: CurrentEdit) => CurrentEdit | undefined;
export type FullOrPartialNavmesh = Partial<Navmesh> &
  Pick<Navmesh, 'links' | 'nodes' | 'clientId' | 'projectId'>;

export type MultiselectNavmeshLink =
  | { clearAll: true }
  | { linkId: number }
  | { ids: number[]; operation: 'add' | 'subtract' };

export type Editing = {
  editCount: number;
  tool: ToolType;
  setTool: (tool: ToolType) => void;
  locatedAssetEdits: Record<string, LocatedAssetEdit>;
  dispatchLocatedAssetEdit: (update: LocatedAssetsUpdate) => void;
  portableAssetEdits: Record<string, PortableAssetEdit>;
  dispatchPortableAssetEdit: (update: PortableAssetsUpdate) => void;
  portableAssetGroupEdits: Record<string, PortableAssetGroupEdit>;
  dispatchPortableAssetGroupEdit: (update: PortableAssetGroupUpdate) => void;
  untrackedAssetEdits: Record<string, UntrackedAssetEdit>;
  dispatchUntrackedAssetEdit: (update: UntrackedAssetsUpdate) => void;
  navGraphWithEdits: { navGraph: EditModeNavGraph; version: number };
  setSourceNavGraph: (soucre: NavGraph) => void;
  emissionLimitEdits: Record<string, EmissionLimitEdit>;
  dispatchEmissionLimitEdit: (update: EmissionLimitUpdate) => void;
  servieConfigEdits: Record<string, ServiceConfigEdit>;
  dispatchServiceConfigEdit: (update: ServiceConfigUpdate) => void;
  microfenceEdits: Record<string, MicrofenceEdit>;
  dispatchMircofenceEdit: (update: MicrofenceUpdate) => void;
  microfencePairEdits: Record<string, MicrofencePairEdit>;
  dispatchMircofencePairEdit: (update: MicrofencePairUpdate) => void;
  currentEdit: CurrentEdit | undefined;
  setCurrentEdit: (edit?: CurrentEdit | SetCurrentEditFn) => void;
  bulkEdit: BulkEdit | undefined;
  setBulkEdit: (edit?: BulkEdit) => void;
  currentDeletion: CurrentDeletion | undefined;
  setCurrentDeletion: (deletion?: CurrentDeletion) => void;
  multiselectedNavmeshLinkIds: Record<number, boolean>;
  multiselectedNavmeshLinkCount: number;
  dispatchMultiselectNavmeshLinkId: (update: MultiselectNavmeshLink) => void;
  selectBoxMode: 'add' | 'subtract' | undefined;
};

const initalTool: ToolType = 'INFO';

export const EditingContext = createContext<Editing>({
  editCount: 0,
  tool: initalTool,
  setTool: VOID,
  locatedAssetEdits: {},
  dispatchLocatedAssetEdit: VOID,
  portableAssetEdits: {},
  dispatchPortableAssetEdit: VOID,
  portableAssetGroupEdits: {},
  dispatchPortableAssetGroupEdit: VOID,
  untrackedAssetEdits: {},
  dispatchUntrackedAssetEdit: VOID,
  navGraphWithEdits: { navGraph: new EditModeNavGraph(), version: -1 },
  setSourceNavGraph: VOID,
  emissionLimitEdits: {},
  dispatchEmissionLimitEdit: VOID,
  servieConfigEdits: {},
  dispatchServiceConfigEdit: VOID,
  microfenceEdits: {},
  dispatchMircofenceEdit: VOID,
  microfencePairEdits: {},
  dispatchMircofencePairEdit: VOID,
  currentEdit: undefined,
  setCurrentEdit: VOID,
  bulkEdit: undefined,
  setBulkEdit: VOID,
  currentDeletion: undefined,
  setCurrentDeletion: VOID,
  multiselectedNavmeshLinkIds: {},
  multiselectedNavmeshLinkCount: 0,
  dispatchMultiselectNavmeshLinkId: VOID,
  selectBoxMode: undefined,
});

export const EditingProvider = ({ children }: { children: ReactNode }) => {
  const [navGraphWithEdits, setNavGraphWithEdits] = useState({
    navGraph: new EditModeNavGraph(),
    version: -1,
  });
  const setSourceNavGraph = useCallback(
    (source: NavGraph) => {
      navGraphWithEdits.navGraph.setSource(source);
    },
    [navGraphWithEdits.navGraph],
  );
  useEffect(() => {
    if (!navGraphWithEdits.navGraph.onChange) {
      navGraphWithEdits.navGraph.onChange = () => {
        setNavGraphWithEdits(prev => ({ ...prev, version: prev.version + 1 }));
      };
    }
  }, [navGraphWithEdits]);

  const [tool, setTool] = useState<ToolType>(initalTool);
  const [locatedAssetEdits, dispatchLocatedAssetEdit] = useReducer(
    (state: Record<string, LocatedAssetEdit>, update: LocatedAssetsUpdate) => {
      if ('clearAll' in update) return {};

      const newState = { ...state };
      const updates = Array.isArray(update) ? update : [update];
      updates.forEach(update => {
        newState[update.uuid] = 'revert' in update ? undefined : update;
      });

      return newState;
    },
    {},
  );
  const [portableAssetEdits, dispatchPortableAssetEdit] = useReducer(
    (state: Record<string, PortableAssetEdit>, update: PortableAssetsUpdate) => {
      if ('clearAll' in update) return {};

      const newState = { ...state };
      const updates = Array.isArray(update) ? update : [update];
      updates.forEach(update => {
        newState[update.uuid] = 'revert' in update ? undefined : update;
      });
      return newState;
    },
    {},
  );
  const [portableAssetGroupEdits, dispatchPortableAssetGroupEdit] = useReducer(
    (state: Record<string, PortableAssetGroupEdit>, update: PortableAssetGroupUpdate) => {
      if ('clearAll' in update) return {};

      const newState = { ...state };
      const updates = Array.isArray(update) ? update : [update];
      updates.forEach(update => {
        newState[update.id] = 'revert' in update ? undefined : update;
      });
      return newState;
    },
    {},
  );
  const [untrackedAssetEdits, dispatchUntrackedAssetEdit] = useReducer(
    (state: Record<string, UntrackedAssetEdit>, update: UntrackedAssetsUpdate) => {
      if ('clearAll' in update) return {};

      const newState = { ...state };
      const updates = Array.isArray(update) ? update : [update];
      updates.forEach(update => {
        newState[update.uuid] = 'revert' in update ? undefined : update;
      });
      return newState;
    },
    {},
  );
  const [emissionLimitEdits, dispatchEmissionLimitEdit] = useReducer(
    (state: Record<string, EmissionLimitEdit>, update: EmissionLimitUpdate) => {
      if ('clearAll' in update) return {};

      const newState = { ...state };
      const updates = Array.isArray(update) ? update : [update];
      updates.forEach(update => {
        newState[update.uuid] = 'revert' in update ? undefined : update;
      });
      return newState;
    },
    {},
  );
  const [microfenceEdits, dispatchMircofenceEdit] = useReducer(
    (state: Record<string, MicrofenceEdit>, update: MicrofenceUpdate) => {
      if ('clearAll' in update) return {};

      const newState = { ...state };
      const updates = Array.isArray(update) ? update : [update];
      updates.forEach(update => {
        newState[update.id] = 'revert' in update ? undefined : update;
      });
      return newState;
    },
    {},
  );
  const [microfencePairEdits, dispatchMircofencePairEdit] = useReducer(
    (state: Record<string, MicrofencePairEdit>, update: MicrofencePairUpdate) => {
      if ('clearAll' in update) return {};

      const newState = { ...state };
      const updates = Array.isArray(update) ? update : [update];
      updates.forEach(update => {
        newState[update.id] = 'revert' in update ? undefined : update;
      });
      return newState;
    },
    {},
  );
  const [currentEdit, setCurrentEditState] = useState<CurrentEdit>();
  const [bulkEdit, setBulkEditState] = useState<BulkEdit>();

  const setCurrentEdit = (edit?: CurrentEdit | SetCurrentEditFn) => {
    setCurrentEditState(edit);
    if (edit) {
      setBulkEditState(undefined);
    }
  };
  const setBulkEdit = (edit?: BulkEdit) => {
    setBulkEditState(edit);
    if (edit) {
      setCurrentEditState(undefined);
    }
  };

  const [currentDeletion, setCurrentDeletion] = useState<CurrentDeletion>();

  const [servieConfigEdits, dispatchServiceConfigEdit] = useReducer(
    (state: Record<string, ServiceConfigEdit>, update: ServiceConfigUpdate) => {
      if ('clearAll' in update) return {};

      return { ...state, [update.id]: 'revert' in update ? undefined : update };
    },
    {},
  );

  const [multiselectedNavmeshLinkIds, dispatchMultiselectNavmeshLinkId] = useReducer(
    (state: Record<number, boolean>, update: MultiselectNavmeshLink) => {
      if ('clearAll' in update) {
        return {};
      }
      if ('ids' in update) {
        return {
          ...state,
          ...Object.fromEntries(update.ids.map(id => [id.toString(), update.operation === 'add'])),
        };
      }
      return {
        ...state,
        [update.linkId]: !state[update.linkId],
      };
    },
    {},
  );
  const multiselectedNavmeshLinkCount = Object.entries(multiselectedNavmeshLinkIds).filter(
    ([, isSelected]) => isSelected,
  ).length;

  const [ctrlKeyDown, setCtrlKeyDown] = useState(false);
  const [shiftKeyDown, setShiftKeyDown] = useState(false);
  const selectBoxMode = ctrlKeyDown && shiftKeyDown ? 'subtract' : shiftKeyDown ? 'add' : undefined;

  useEffect(() => {
    const handleKeyDownForSelectbox = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.ctrlKey) {
        setCtrlKeyDown(true);
      }
      if (e.key === 'Shift') {
        setShiftKeyDown(true);
      }
    };
    const handleKeyUpForSelectbox = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        setCtrlKeyDown(false);
      } else if (e.key === 'Shift') {
        setShiftKeyDown(false);
      }
    };
    window.addEventListener('keydown', handleKeyDownForSelectbox);
    window.addEventListener('keyup', handleKeyUpForSelectbox);
    return () => {
      window.removeEventListener('keydown', handleKeyDownForSelectbox);
      window.removeEventListener('keyup', handleKeyUpForSelectbox);
    };
  }, [setCtrlKeyDown, setShiftKeyDown]);

  const editCount =
    Object.values(locatedAssetEdits).filter(isDefined).length +
    Object.values(portableAssetEdits).filter(isDefined).length +
    Object.values(portableAssetGroupEdits).filter(isDefined).length +
    Object.values(untrackedAssetEdits).filter(isDefined).length +
    Object.values(emissionLimitEdits).filter(isDefined).length +
    Object.values(servieConfigEdits).filter(isDefined).length +
    Object.values(microfenceEdits).filter(isDefined).length +
    Object.values(microfencePairEdits).filter(isDefined).length +
    navGraphWithEdits.navGraph.editCount;

  return (
    <EditingContext.Provider
      value={{
        editCount,
        tool,
        setTool,
        locatedAssetEdits,
        dispatchLocatedAssetEdit,
        portableAssetEdits,
        dispatchPortableAssetEdit,
        portableAssetGroupEdits,
        dispatchPortableAssetGroupEdit,
        untrackedAssetEdits,
        dispatchUntrackedAssetEdit,
        navGraphWithEdits,
        setSourceNavGraph,
        servieConfigEdits,
        dispatchServiceConfigEdit,
        emissionLimitEdits,
        dispatchEmissionLimitEdit,
        microfenceEdits,
        dispatchMircofenceEdit,
        microfencePairEdits,
        dispatchMircofencePairEdit,
        currentEdit,
        setCurrentEdit,
        bulkEdit,
        setBulkEdit,
        currentDeletion,
        setCurrentDeletion,
        multiselectedNavmeshLinkIds,
        multiselectedNavmeshLinkCount,
        dispatchMultiselectNavmeshLinkId,
        selectBoxMode,
      }}
    >
      {children}
    </EditingContext.Provider>
  );
};

export const useEditingData = () => useContext(EditingContext);
